import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ResponsiveLayout from '../components/ResponsiveLayout';
import { Baby, BarChart3, Brain, Calendar, ChevronRight, ClipboardList, Crown, Eye, FileText, Home, IndianRupee, Info, Phone, RefreshCw, Shield, Star, TrendingUp, UserCheck, Users } from 'lucide-react';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalEarnings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [rateCard, setRateCard] = useState(null);
  const [childrenByPlan, setChildrenByPlan] = useState({ premium: 0, basic: 0 });
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate, period]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('principal_token');
      
      const [earningsRes, rateCardRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/earnings-summary?token=${token}&period=${period}`),
        axios.get(`${BACKEND_URL}/api/principal/rate-card?token=${token}`)
      ]);
      
      setEarnings(earningsRes.data.earnings || {});
      setChildrenByPlan(earningsRes.data.children_by_plan || { premium: 0, basic: 0 });
      setRateCard(rateCardRes.data);
    } catch (error) {
      console.error('Error loading earnings:', error);
      if (error.response?.status === 401) {
        navigate('/principal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sanjaya-navy"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="principal-earnings">
      
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <IndianRupee className="w-7 h-7 text-emerald-600" />
                Earnings Overview
              </h1>
              <p className="text-slate-500">Track your income from sessions</p>
            </div>
            <div className="flex gap-2">
              <select 
                value={period} 
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Time</option>
                <option value="month">This Month</option>
                <option value="week">This Week</option>
              </select>
              <Button variant="outline" onClick={loadData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Total Earnings */}
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm mb-1">Total Earnings</p>
                  <p className="text-4xl font-bold">{formatCurrency(earnings?.total_earnings)}</p>
                  <p className="text-emerald-100 text-sm mt-2">
                    From {earnings?.total_sessions || 0} sessions
                  </p>
                </div>
                <div className="text-right">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-10 h-10" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Earnings by Plan */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Crown className="w-7 h-7 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Premium Plan</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {formatCurrency(earnings?.earnings_by_plan?.premium)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {earnings?.sessions_by_plan?.premium || 0} sessions • {childrenByPlan.premium} children
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Star className="w-7 h-7 text-sanjaya-navy" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-500">Basic Plan</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {formatCurrency(earnings?.earnings_by_plan?.basic)}
                    </p>
                    <p className="text-sm text-slate-500">
                      {earnings?.sessions_by_plan?.basic || 0} sessions • {childrenByPlan.basic} children
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Rate Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Info className="w-5 h-5 text-sanjaya-navy" />
                Principal Rate Card
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Plan</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Description</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Full Rate</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Trial Rate (50%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b hover:bg-amber-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-500" />
                          <span className="font-medium">Premium</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm">
                        Daily conversations + 2x/month counseling
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                        {formatCurrency(rateCard?.principal_rates?.premium?.full)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500">
                        {formatCurrency(rateCard?.principal_rates?.premium?.trial)}
                      </td>
                    </tr>
                    <tr className="hover:bg-blue-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 text-blue-500" />
                          <span className="font-medium">Basic</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm">
                        3x/week conversations
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-emerald-600">
                        {formatCurrency(rateCard?.principal_rates?.basic?.full)}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-500">
                        {formatCurrency(rateCard?.principal_rates?.basic?.trial)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <strong>Trial Period:</strong> First {rateCard?.trial_sessions || 7} sessions are at 50% rate. 
                After trial, parents choose to subscribe at full rate.
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default PrincipalEarnings;
